import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { IoShieldCheckmarkOutline } from "react-icons/io5";

import { termsOfService } from "../data/termsOfService";
import { Footer } from "../components";

/* ─── Sticky back + title bar ─────────────────────────────────────────────── */
const TopBar = ({ onBack }) => (
  <header
    className="sticky top-0 z-50 border-b backdrop-blur-md"
    style={{
      background: "var(--background-primary)",
      borderColor: "var(--border-color)",
    }}
  >
    <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-semibold rounded-lg px-3 py-2"
        style={{
          background: "var(--background-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-color)",
          transition: "background 0.2s, color 0.2s, transform 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "#059669";
          e.currentTarget.style.transform = "translateX(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--text-primary)";
          e.currentTarget.style.transform = "translateX(0)";
        }}
      >
        <IoArrowBack size={16} />
        Back to Home
      </button>

      <div className="h-5 w-px" style={{ background: "var(--border-color)" }} />

      <span
        className="text-sm font-semibold tracking-wide"
        style={{ color: "var(--text-secondary)" }}
      >
        WaBM Partner · Terms of Service
      </span>
    </div>
  </header>
);

/* ─── Sidebar table of contents ───────────────────────────────────────────── */
const TableOfContents = ({ sections, activeId }) => (
  <nav className="hidden lg:block sticky top-[73px] self-start">
    <p
      className="text-xs font-bold uppercase tracking-widest mb-3"
      style={{ color: "var(--text-secondary)" }}
    >
      Contents
    </p>
    <ul className="space-y-1">
      {sections.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            className="block text-sm rounded-lg px-3 py-1.5 transition-all duration-200"
            style={{
              color: activeId === s.id ? "#059669" : "var(--text-secondary)",
              background:
                activeId === s.id ? "rgba(5,150,105,0.10)" : "transparent",
              fontWeight: activeId === s.id ? 600 : 400,
            }}
          >
            {s.title}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

/* ─── Individual section card ─────────────────────────────────────────────── */
const Section = ({ section }) => (
  <section
    id={section.id}
    className="rounded-2xl border p-6 scroll-mt-24"
    style={{
      background: "var(--background-primary)",
      borderColor: "var(--border-color)",
    }}
  >
    <h2
      className="text-lg font-bold mb-4 pb-3 border-b"
      style={{
        color: "var(--text-primary)",
        borderColor: "var(--border-color)",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      {section.title}
    </h2>

    <ul className="space-y-3">
      {section.content.map((para, i) => (
        <li key={i} className="flex gap-3">
          <span
            className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full"
            style={{ background: "#059669", marginTop: "8px" }}
          />
          <p
            className="text-sm leading-relaxed"
            style={{ color: "var(--text-primary)", opacity: 0.85 }}
          >
            {para}
          </p>
        </li>
      ))}
    </ul>
  </section>
);

/* ─── Main page ────────────────────────────────────────────────────────────── */
const TermsOfService = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("");

  /* Track which section is in view for the ToC highlight */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );

    termsOfService.sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--background-main)" }}
    >
      <TopBar onBack={() => navigate("/")} />

      {/* ── Hero banner ── */}
      <div
        className="relative overflow-hidden border-b"
        style={{
          background:
            "linear-gradient(135deg, rgba(5,150,105,0.08) 0%, rgba(79,70,229,0.06) 100%)",
          borderColor: "var(--border-color)",
        }}
      >
        {/* subtle dot grid */}
        <svg
          className="pointer-events-none absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: 0.35 }}
        >
          <defs>
            <pattern
              id="tos-dots"
              x="0"
              y="0"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1.5" cy="1.5" r="1.5" fill="#94a3b8" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#tos-dots)" />
        </svg>

        <div className="relative max-w-4xl mx-auto px-6 py-14 text-center">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5"
            style={{
              background: "rgba(5,150,105,0.12)",
              border: "1px solid rgba(5,150,105,0.25)",
            }}
          >
            <IoShieldCheckmarkOutline size={28} color="#059669" />
          </div>

          <h1
            className="text-4xl font-bold mb-3"
            style={{
              fontFamily: "Montserrat, sans-serif",
              color: "var(--text-primary)",
            }}
          >
            Terms of Service
          </h1>

          <p
            className="text-base max-w-xl mx-auto leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            Please read these terms carefully before using the{" "}
            <strong style={{ color: "#059669" }}>
              {termsOfService.productName}
            </strong>
            . By continuing to access the Dashboard, you agree to these terms.
          </p>

          {/* Meta pills */}
          <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{
                background: "rgba(5,150,105,0.12)",
                color: "#059669",
                border: "1px solid rgba(5,150,105,0.25)",
              }}
            >
              Last updated: {termsOfService.tos.lastUpdated}
            </span>
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{
                background: "var(--background-primary)",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-color)",
              }}
            >
              {termsOfService.companyName}
            </span>
          </div>
        </div>
      </div>

      {/* ── Body: ToC sidebar + sections ── */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">
        <div className="flex gap-10 items-start">
          {/* Sidebar ToC (desktop only) */}
          <div className="w-64 shrink-0">
            <TableOfContents
              sections={termsOfService.sections}
              activeId={activeId}
            />
          </div>

          {/* Sections */}
          <div className="flex-1 flex flex-col gap-5 min-w-0">
            {termsOfService.sections.map((section) => (
              <Section key={section.id} section={section} />
            ))}

            {/* Bottom CTA */}
            <div
              className="rounded-2xl border p-6 text-center mt-2"
              style={{
                background:
                  "linear-gradient(135deg, rgba(5,150,105,0.07) 0%, rgba(79,70,229,0.05) 100%)",
                borderColor: "rgba(5,150,105,0.2)",
              }}
            >
              <p
                className="text-sm font-semibold mb-1"
                style={{ color: "var(--text-primary)" }}
              >
                Questions about these terms?
              </p>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                Reach us at{" "}
                <a
                  href={`mailto:${termsOfService.contactEmail}`}
                  style={{
                    color: "#059669",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.textDecoration = "none")
                  }
                >
                  {termsOfService.contactEmail}
                </a>{" "}
                — we respond within 2–3 business days.
              </p>

              <button
                onClick={() => navigate("/")}
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl"
                style={{
                  background:
                    "linear-gradient(135deg, #059669 0%, #4f46e5 100%)",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  transition: "opacity 0.2s, transform 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <IoArrowBack size={15} />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;
